<template>
  <Modal>
    <div slot="header">
      <div class="modal-header__content">
        <h3 class="text-bold text-lg mb-0 text-white">Import Players</h3>
        <button class="modal-header__close" @click="toggle">
          <svg-icon icon="x"></svg-icon>
        </button>
      </div>
    </div>
    <div slot="body">
      <!-- Upload State -->
      <div v-if="state === 'upload'" class="import-modal">
        <div
          class="import-modal__dropzone"
          :class="{ 'import-modal__dropzone--active': isDragging }"
          @dragover.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false"
          @drop.prevent="handleDrop"
          @click="$refs.fileInput.click()">
          <svg-icon icon="upload-cloud" class="inline-block"></svg-icon>
          <p class="mt-8 text-bold">Drag & drop a CSV file here</p>
          <p class="mt-4 text-sm text-muted">or click to browse</p>
          <input
            ref="fileInput"
            type="file"
            accept=".csv,.txt"
            class="import-modal__input"
            @change="handleFileSelect" />
        </div>
        <p v-if="parseError" class="mt-8 text-error text-sm">
          {{ parseError }}
        </p>
        <div v-if="showAddPlayer" class="import-modal__divider">
          <span>or</span>
        </div>
        <div v-if="showAddPlayer" class="import-modal__add-single">
          <router-link to="/players/new" class="btn btn--primary">
            Add single player
          </router-link>
        </div>
      </div>

      <!-- Preview State -->
      <div v-if="state === 'preview'" class="import-modal">
        <div class="import-modal__summary">
          <p class="text-bold mb-8">{{ file.name }}</p>
          <div class="import-modal__stats">
            <div class="import-modal__stat">
              <span class="text-muted">Total rows</span>
              <span class="text-bold">{{ preview.totalRows }}</span>
            </div>
            <div class="import-modal__stat">
              <span class="text-muted">Matched (existing)</span>
              <span class="text-bold">{{ preview.matched }}</span>
            </div>
            <div class="import-modal__stat">
              <span class="text-muted">New players</span>
              <span class="text-bold">{{ preview.newPlayers }}</span>
            </div>
          </div>
        </div>
        <div v-if="preview.rows.length > 0" class="import-modal__preview mt-16">
          <table class="Table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in preview.rows" :key="index">
                <td>{{ row.name }}</td>
                <td>{{ row.email }}</td>
                <td>
                  <span :class="row.isExisting ? 'text-muted' : 'text-success'">
                    {{ row.isExisting ? 'Existing' : 'New' }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
          <p v-if="preview.totalRows > 10" class="mt-8 text-sm text-muted">
            Showing first 10 of {{ preview.totalRows }} rows
          </p>
        </div>
        <div class="import-modal__actions mt-16">
          <button class="btn btn--outlined" @click="resetState">Cancel</button>
          <button class="btn btn--primary" @click="handleImport">
            Import Players
          </button>
        </div>
      </div>

      <!-- Result State -->
      <div v-if="state === 'result'" class="import-modal">
        <div class="import-modal__summary">
          <h4 class="text-bold mb-8">Import Complete</h4>
          <div class="import-modal__stats">
            <div class="import-modal__stat">
              <span class="text-muted">Created</span>
              <span class="text-bold">{{ result.created || 0 }}</span>
            </div>
            <div class="import-modal__stat">
              <span class="text-muted">Matched</span>
              <span class="text-bold">{{ result.matched || 0 }}</span>
            </div>
            <div class="import-modal__stat">
              <span class="text-muted">Updated</span>
              <span class="text-bold">{{ result.updated || 0 }}</span>
            </div>
            <div v-if="eventId" class="import-modal__stat">
              <span class="text-muted">Submissions created</span>
              <span class="text-bold">{{
                result.submissions_created || 0
              }}</span>
            </div>
            <div v-if="eventId" class="import-modal__stat">
              <span class="text-muted">Skipped</span>
              <span class="text-bold">{{ result.skipped || 0 }}</span>
            </div>
          </div>
        </div>
        <div
          v-if="result.errors && result.errors.length > 0"
          class="import-modal__errors mt-16">
          <h4 class="text-bold mb-8 text-error">Errors</h4>
          <ul>
            <li
              v-for="(error, index) in result.errors"
              :key="index"
              class="text-sm">
              Row {{ error.row }}: {{ error.message }}
            </li>
          </ul>
        </div>
        <div class="import-modal__actions mt-16">
          <button class="btn btn--primary" @click="toggle">Close</button>
        </div>
      </div>
    </div>
  </Modal>
</template>

<script>
  import Modal from '@/components/Modal';

  export default {
    name: 'ImportPlayersModal',
    components: { Modal },
    props: {
      toggle: {
        type: Function,
        required: true,
      },
      eventId: {
        type: [Number, String],
        default: null,
      },
      showAddPlayer: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        state: 'upload',
        isDragging: false,
        file: null,
        parseError: null,
        preview: {
          totalRows: 0,
          matched: 0,
          newPlayers: 0,
          rows: [],
        },
        result: {},
      };
    },
    methods: {
      handleDrop(e) {
        this.isDragging = false;
        const files = e.dataTransfer.files;
        if (files.length > 0) {
          this.processFile(files[0]);
        }
      },
      handleFileSelect(e) {
        const files = e.target.files;
        if (files.length > 0) {
          this.processFile(files[0]);
        }
      },
      processFile(file) {
        const validTypes = [
          'text/csv',
          'text/plain',
          'application/vnd.ms-excel',
        ];
        if (
          !validTypes.includes(file.type) &&
          !file.name.endsWith('.csv') &&
          !file.name.endsWith('.txt')
        ) {
          this.parseError = 'Please select a CSV or TXT file.';
          return;
        }
        this.file = file;
        this.parseError = null;

        const reader = new FileReader();
        reader.onload = (e) => {
          this.parseCSV(e.target.result);
        };
        reader.onerror = () => {
          this.parseError = 'Could not read the file.';
        };
        reader.readAsText(file);
      },
      parseCSV(text) {
        const lines = text
          .split(/\r?\n/)
          .filter((line) => line.trim().length > 0);
        if (lines.length < 2) {
          this.parseError =
            'CSV file must contain a header row and at least one data row.';
          return;
        }

        const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());
        const nameIndex = headers.findIndex(
          (h) => h === 'name' || h === 'first_name' || h === 'full_name',
        );
        const emailIndex = headers.findIndex(
          (h) => h === 'email' || h === 'e-mail',
        );

        const existingPlayers = this.$store.state.players.all || [];
        const existingEmails = existingPlayers.map((p) =>
          (p.email || '').toLowerCase(),
        );

        const dataRows = lines.slice(1);
        let matched = 0;
        let newPlayers = 0;
        const previewRows = [];

        dataRows.forEach((line, index) => {
          const cols = line.split(',').map((c) => c.trim());
          const name = nameIndex >= 0 ? cols[nameIndex] || '' : '';
          const email = emailIndex >= 0 ? cols[emailIndex] || '' : '';
          const isExisting =
            email && existingEmails.includes(email.toLowerCase());

          if (isExisting) {
            matched++;
          } else {
            newPlayers++;
          }

          if (index < 10) {
            previewRows.push({ name, email, isExisting });
          }
        });

        this.preview = {
          totalRows: dataRows.length,
          matched,
          newPlayers,
          rows: previewRows,
        };
        this.state = 'preview';
      },
      handleImport() {
        const formData = new FormData();
        formData.append('file', this.file);

        if (this.eventId) {
          this.$store
            .dispatch('events/IMPORT_PLAYERS_TO_EVENT', {
              eventId: this.eventId,
              formData,
            })
            .then((res) => {
              this.result = res;
              this.state = 'result';
            })
            .catch(() => {
              this.state = 'upload';
              this.parseError = 'Import failed. Please try again.';
            });
        } else {
          this.$store
            .dispatch('players/IMPORT_PLAYERS', formData)
            .then((res) => {
              this.result = res;
              this.state = 'result';
            })
            .catch(() => {
              this.state = 'upload';
              this.parseError = 'Import failed. Please try again.';
            });
        }
      },
      resetState() {
        this.state = 'upload';
        this.file = null;
        this.parseError = null;
        this.preview = { totalRows: 0, matched: 0, newPlayers: 0, rows: [] };
        if (this.$refs && this.$refs.fileInput) {
          this.$refs.fileInput.value = null;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .import-modal {
    padding: 16px 0;

    &__dropzone {
      border: 2px dashed #d0d5dd;
      border-radius: 8px;
      padding: 40px 24px;
      text-align: center;
      cursor: pointer;
      transition: border-color 0.2s, background-color 0.2s;

      &:hover,
      &--active {
        border-color: #2563eb;
        background-color: rgba(37, 99, 235, 0.04);
      }
    }

    &__input {
      display: none;
    }

    &__stats {
      display: flex;
      gap: 24px;
      flex-wrap: wrap;
    }

    &__stat {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    &__errors {
      max-height: 150px;
      overflow-y: auto;

      ul {
        list-style: none;
        padding: 0;
        margin: 0;
      }

      li {
        padding: 4px 0;
      }
    }

    &__actions {
      display: flex;
      justify-content: flex-end;
      gap: 12px;
    }

    &__preview {
      max-height: 300px;
      overflow-y: auto;
    }

    &__divider {
      display: flex;
      align-items: center;
      margin: 16px 0;

      &::before,
      &::after {
        content: '';
        flex: 1;
        height: 1px;
        background-color: #d0d5dd;
      }

      span {
        padding: 0 12px;
        color: #98a2b3;
        font-size: 14px;
      }
    }

    &__add-single {
      text-align: center;
    }
  }
</style>
